import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate(loc.state?.from?.pathname || "/", { replace: true });
    } catch (e) {
      // Surface the real error: backend message > axios message > generic
      const backendMsg = formatApiError(e.response?.data?.detail);
      const networkMsg = e.message && e.message !== "Network Error"
        ? e.message
        : "Cannot reach the server. Please check your internet or try again shortly.";
      setError(e.response ? backendMsg : networkMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: brand + photo */}
      <div className="hidden lg:flex lg:w-1/2 mptg-sidebar items-center justify-center p-12 relative overflow-hidden">
        {/* Background photo of Indian corporate professionals */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1600&q=80')",
            opacity: 0.28,
          }}
          aria-hidden
          data-testid="login-hero-image"
        />
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1A33]/85 via-[#1B2A4A]/75 to-[#0F1A33]/90" aria-hidden />

        <div className="max-w-md relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <BrandLogo size={48} fallbackChar="M" tone="dark" />
            <div>
              <div className="text-white font-heading text-2xl font-bold tracking-tight">MPTG-CRM</div>
              <div className="text-slate-300 text-xs uppercase tracking-[0.2em] mt-1">MAS Callnet India</div>
            </div>
          </div>
          <div className="text-[#2E86C1] text-xs font-bold uppercase tracking-[0.25em] mb-3" data-testid="login-tagline">
            India's only B2B service CRM platform
          </div>
          <h1 className="text-white text-4xl lg:text-5xl font-heading font-bold tracking-tight leading-tight mb-6">
            One system of record for every enterprise relationship.
          </h1>
          <p className="text-slate-200 text-base leading-relaxed">
            Contracts, KPIs, billing, business reviews and market intelligence — in one place, across 100+ clients and 300+ campaigns.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-12">
            {[
              ["100+", "Client accounts"],
              ["300+", "Campaigns"],
              ["50", "Concurrent users"],
              ["6", "Role permissions"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="text-[#2E86C1] font-heading text-3xl font-bold">{n}</div>
                <div className="text-slate-300 text-xs uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <BrandLogo size={40} fallbackChar="M" />
            <div>
              <div className="font-heading font-bold text-lg tracking-tight">MPTG-CRM</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">MAS Callnet India</div>
            </div>
          </div>
          <div className="lg:hidden text-[10px] text-[#2E86C1] font-bold uppercase tracking-widest mb-4" data-testid="login-tagline-mobile">
            India's only B2B service CRM platform
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="text-slate-500 text-sm mt-1">Enter your credentials to access the CRM.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600" htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600" htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                required
                data-testid="login-password"
              />
            </div>
            {error && <div className="text-sm text-[#C0392B]" data-testid="login-error">{error}</div>}
            <Button
              type="submit"
              className="w-full bg-[#2E86C1] hover:bg-[#2471A3] text-white"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center" aria-hidden>
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-widest">
              <span className="bg-white px-2 text-slate-400">or</span>
            </div>
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            type="button"
            onClick={() => {
              // Always derive redirect from the live origin — Emergent expects the
              // exact domain the user is currently on.
              const redirectUrl = window.location.origin + "/";
              window.location.href =
                "https://auth.emergentagent.com/?redirect=" +
                encodeURIComponent(redirectUrl);
            }}
            className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 rounded-md py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            Sign in with Google
          </button>
          <div className="text-[11px] text-slate-400 text-center mt-2">
            New Google sign-ups need admin approval before they can access the CRM.
          </div>
        </div>
      </div>
    </div>
  );
}
